import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'

interface DrawerProps {
    open: boolean
    onClose: () => void
    children: React.ReactNode
    title?: string
    width?: string
}

const Drawer: React.FC<DrawerProps> = ({
    open,
    onClose,
    children,
    width = '600px',
}) => {
    // Prevent body scroll when drawer is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }

        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [open])

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        if (open) {
            document.addEventListener('keydown', handleEscape)
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
        }
    }, [open, onClose])

    if (!open) return null

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className={`
                    ml-auto h-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out
                    ${open ? 'translate-x-0' : 'translate-x-full'}
                    flex flex-col
                `}
                style={{ width }}
            >
                {/* Content */}
                <div className="flex-1 overflow-auto">{children}</div>
            </div>
        </div>,
        document.body
    )
}

export default Drawer
